import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DeliveryScheduleModel } from '../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../components/forms/Pagination';
import { useState, useMemo } from 'react';
import { useTheme } from '@mui/material';

interface ValidationResultTableProps {
  data: DeliveryScheduleModel[];
  type: 'updated' | 'created' | 'deleted';
}

function ValidationResultTable({ data, type }: ValidationResultTableProps) {
  const { t } = useTranslation('odsmanagement');
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const filteredAndPaginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    
    return {
      items: data.slice(startIndex, endIndex),
      totalItems: data.length,
    };
  }, [data, page]);

  const maxPages = Math.ceil(filteredAndPaginatedData.totalItems / pageSize);

  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          {t('noChangesInThisCategory')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer sx={{ 
        py: '1em',
        overflow: 'auto',
      }}>
        <Table stickyHeader 
          size="small">
          <TableHead sx={{ backgroundColor: theme.palette.custom?.gray?.[200] || '#f5f5f5' }}>
            <TableRow>
              { type != 'created' &&<TableCell sx={{ whiteSpace: 'nowrap' }}>{t('id')}</TableCell> }
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('store')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('orderType')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('cutOffDate')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('deliveryDate')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('supplier')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndPaginatedData.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} 
                  align="center">
                  <Typography variant="textSM" 
                    color="text.secondary" 
                    sx={{ py: 4 }}>
                    {t('noDataAvailable')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndPaginatedData.items.map((row, index) => (
                <TableRow key={`${type}-${index}`}>
                  { type != 'created' &&
                        <TableCell>
                          <Typography variant="textSM">
                            {row.id}
                          </Typography>
                        </TableCell>
                  }
                  <TableCell>
                    <Typography variant="textSM">
                      {row.storeNumber} {row.storeName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="textSM">
                      {row.orderType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="textSM">
                      {row.cutOffDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="textSM">
                      {row.deliveryDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="textSM">
                      {row.supplierNumber}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomPagination 
        useNoDataMessage={false}
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
        disabled={false}
      />
    </>
  );
}

export default ValidationResultTable;